import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeInline } from "@/components/employee/EmployeeInline.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { CompensationRollupValue } from "@/components/sensitive-value/CompensationRollupValue.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import useFormatter, { formatSlackHandle } from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import getTimezones from "@/services/timezone-service.ts";
import {
  ApiColumnConfigType,
  ColumnConfig,
  ColumnDefinition,
  EmployeeColumnId,
} from "@/types/column.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiField,
  ApiFieldTypes,
  ApiFieldValueTypes,
  ApiTableTypes,
} from "@/types/custom-field.types.ts";
import { ApiAppConfigUIEmployeeProfileSection } from "@/types/config.types.ts";
import { getEmployeeCompensationPerTypeTitle } from "@/types/employees/employee-compensation.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployee,
  EmployeeGenders,
  LifecycleStatusTexts,
} from "@/types/employees/employee.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { LinkTo, ListItem, Stack, Text } from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { toZonedTime } from "date-fns-tz";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const DEFAULT_VISIBLE_COLUMNS = [
  EmployeeColumnId.employee_position,
  EmployeeColumnId.department,
  EmployeeColumnId.manager,
];

export const useEmployeeColumns = () => {
  const { t } = useTranslation();
  const { renderDate, renderPartialDate, renderTime, renderCurrency } = useFormatter();
  const [now, setNow] = useState(new Date());
  new Map(getTimezones().map((tz) => [tz.id, tz.title]));
  const { data: appConfig } = useAppConfig();
  const { isFeatureEnabled, canCurrentEmployeeViewField } = usePermissions();
  const { renderFieldDataInCell, getFieldTitle } = useCustomFieldsHook();
  const { fields: positionFields } = useSystemTableConfig(ApiTableTypes.employee_positions, "view");
  const { fields: employmentFields } = useSystemTableConfig(
    ApiTableTypes.employee_employments,
    "view"
  );
  const { fields: compensationFields } = useSystemTableConfig(
    ApiTableTypes.employee_compensations,
    "view"
  );

  const fieldTitle = (
    fields: Pick<ApiField, "type" | "title">[],
    type: ApiFieldTypes,
    fallback: string
  ) => {
    const field = fields.find((item) => item.type === type);
    // Системное «Дата» без сущности в колонках списка неоднозначно — берём только переименование админа
    if (type === ApiFieldTypes.effective_from) {
      return field?.title?.trim() || fallback;
    }
    return field ? getFieldTitle(field) : fallback;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const allSections: ApiAppConfigUIEmployeeProfileSection[] = useMemo(() => {
    return (
      appConfig?.ui?.employee_profile?.tabs?.flatMap((tabWrapper) =>
        tabWrapper.items
          .map((item) => item.section)
          .filter((section): section is ApiAppConfigUIEmployeeProfileSection => section !== null)
      ) ?? []
    );
  }, [appConfig]);

  const allSectionFields = useMemo(
    () => allSections.flatMap((sectionWrapper) => sectionWrapper.fields),
    [allSections]
  );

  const columnDefinitions = useMemo<
    Record<EmployeeColumnId | string, ColumnDef<ApiEmployee>>
  >(() => {
    const columnDefinitionsForCustomFields: Record<string, ColumnDef<ApiEmployee>> = {};

    if (isFeatureEnabled(ApiFeatureType.custom_fields)) {
      allSections.forEach((sectionWrapper) => {
        sectionWrapper.fields.forEach((field) => {
          if (field.type == ApiFieldTypes.custom) {
            columnDefinitionsForCustomFields[field.slug] = {
              id: field.slug,
              accessorKey: field.slug,
              header: getFieldTitle(field),
              cell: (row) => {
                return renderFieldDataInCell(field, row.row.original.custom_fields);
              },
              ...(field.field_type === ApiFieldValueTypes.employee_reference
                ? ColumnSizes.employee
                : ColumnSizes.standard),
              enableSorting: false,
            };
          }
        });
      });
    }

    return {
      [EmployeeColumnId.hired_on_relative]: {
        id: EmployeeColumnId.hired_on_relative,
        accessorKey: EmployeeColumnId.hired_on_relative,
      },
      [EmployeeColumnId.birth_date_relative]: {
        id: EmployeeColumnId.birth_date_relative,
        accessorKey: EmployeeColumnId.birth_date_relative,
      },
      [EmployeeColumnId.employee_name]: {
        id: EmployeeColumnId.employee_name,
        accessorKey: EmployeeColumnId.employee_name,
        header: t("core:full_name"),
        cell: ({ row }) => (
          <EmployeeItem
            employee={row.original}
            showPosition={false}
            size={"md"}
            enableLink={true}
            target={"_self"}
          />
        ),
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.lifecycle_status]: {
        id: EmployeeColumnId.lifecycle_status,
        accessorKey: EmployeeColumnId.lifecycle_status,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_lifecycle_status,
          t("core:lifecycle_status")
        ),
        cell: ({ row }) => {
          const statusTexts = LifecycleStatusTexts();
          return row.original.lifecycle_status ? statusTexts[row.original.lifecycle_status] : "-";
        },
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.middle_name]: {
        id: EmployeeColumnId.middle_name,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_middle_name,
          t("core:middle_name")
        ),
        cell: ({ row }) => row.original.middle_name || "-",
        accessorKey: EmployeeColumnId.middle_name,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.name_pronunciation]: {
        id: EmployeeColumnId.name_pronunciation,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_name_pronunciation,
          t("common:name_pronunciation")
        ),
        cell: ({ row }) => row.original.name_pronunciation || "-",
        accessorKey: EmployeeColumnId.name_pronunciation,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.last_seen]: {
        id: EmployeeColumnId.last_seen,
        header: t("common:last_seen"),
        cell: ({ row }) =>
          row.original.datetime_last_online ? renderDate(row.original.datetime_last_online) : "-",
        accessorKey: EmployeeColumnId.last_seen,
        ...ColumnSizes.date,
        enableSorting: true,
        sortDescFirst: true,
      },
      [EmployeeColumnId.hired_on]: {
        id: EmployeeColumnId.hired_on,
        header: fieldTitle(allSectionFields, ApiFieldTypes.employee_hired_on, t("core:hired_on")),
        cell: ({ row }) =>
          row.original.date_hired_on ? renderDate(row.original.date_hired_on) : "-",
        accessorKey: EmployeeColumnId.hired_on,
        ...ColumnSizes.date,
        enableSorting: true,
        sortDescFirst: true,
      },
      [EmployeeColumnId.birth_date]: {
        id: EmployeeColumnId.birth_date,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_date_birth,
          t("core:birth_date")
        ),
        cell: ({ row }) =>
          row.original.birth_date ? renderPartialDate(row.original.birth_date) : "-",
        accessorKey: EmployeeColumnId.birth_date,
        ...ColumnSizes.date,
        enableSorting: true,
        sortDescFirst: true,
      },
      [EmployeeColumnId.personnel_number]: {
        id: EmployeeColumnId.personnel_number,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_number,
          t("core:employee_number")
        ),
        cell: ({ row }) => (row.original.personnel_number ? row.original.personnel_number : "-"),
        accessorKey: EmployeeColumnId.personnel_number,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.gender]: {
        id: EmployeeColumnId.gender,
        header: fieldTitle(allSectionFields, ApiFieldTypes.employee_gender, t("core:gender")),
        cell: ({ row }) => {
          const gender = EmployeeGenders.find((g) => g.id === row.original.gender);
          return gender ? t(gender.title) : "-";
        },
        accessorKey: EmployeeColumnId.gender,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.address]: {
        id: EmployeeColumnId.address,
        accessorKey: EmployeeColumnId.address,
        header: t("core:address"),
        cell: ({ row }) => {
          const streetAddress = [row.original.address_1, row.original.address_2]
            .filter(Boolean)
            .join(" ");

          const cityLine = [row.original.city, row.original.state, row.original.zip]
            .filter(Boolean)
            .join(" ");

          const country = row.original.country?.title ?? "";

          const parts = [streetAddress, cityLine, country].filter((part) => part.trim().length > 0);

          return parts.length ? parts.join("\n") : "-";
        },
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.address_country]: {
        id: EmployeeColumnId.address_country,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_address_country,
          t("core:country")
        ),
        cell: ({ row }) => row.original.country?.title || "-",
        accessorKey: EmployeeColumnId.address_country,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.address_city]: {
        id: EmployeeColumnId.address_city,
        header: fieldTitle(allSectionFields, ApiFieldTypes.employee_address_city, t("core:city")),
        cell: ({ row }) => row.original.city || "-",
        accessorKey: EmployeeColumnId.address_city,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.address_state]: {
        id: EmployeeColumnId.address_state,
        header: fieldTitle(allSectionFields, ApiFieldTypes.employee_address_state, t("core:state")),
        cell: ({ row }) => row.original.state || "-",
        accessorKey: EmployeeColumnId.address_state,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.address_zip]: {
        id: EmployeeColumnId.address_zip,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_address_zip_code,
          t("core:zip")
        ),
        cell: ({ row }) => row.original.zip || "-",
        accessorKey: EmployeeColumnId.address_zip,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.address_line_1]: {
        id: EmployeeColumnId.address_line_1,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_address_address_line_1,
          t("core:address_line_1")
        ),
        cell: ({ row }) => row.original.address_1 || "-",
        accessorKey: EmployeeColumnId.address_line_1,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.address_line_2]: {
        id: EmployeeColumnId.address_line_2,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_address_address_line_2,
          t("core:address_line_2")
        ),
        cell: ({ row }) => row.original.address_2 || "-",
        accessorKey: EmployeeColumnId.address_line_2,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.termination_date]: {
        id: EmployeeColumnId.termination_date,
        header: t("core:termination_date"),
        cell: ({ row }) =>
          row.original.date_termination ? renderDate(row.original.date_termination) : "-",
        accessorKey: EmployeeColumnId.termination_date,
        ...ColumnSizes.date,
        enableSorting: true,
        sortDescFirst: true,
      },
      [EmployeeColumnId.email]: {
        id: EmployeeColumnId.email,
        header: fieldTitle(allSectionFields, ApiFieldTypes.employee_email, t("core:email")),
        cell: ({ row }) => (row.original.email ? row.original.email : "-"),
        accessorKey: EmployeeColumnId.email,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.email_personal]: {
        id: EmployeeColumnId.email_personal,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_email_personal,
          t("core:email_personal")
        ),
        cell: ({ row }) => (row.original.email_personal ? row.original.email_personal : "-"),
        accessorKey: EmployeeColumnId.email_personal,
        ...ColumnSizes.standard,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.phone]: {
        id: EmployeeColumnId.phone,
        header: fieldTitle(allSectionFields, ApiFieldTypes.employee_phone, t("core:phone")),
        cell: ({ row }) => (row.original.phone ? row.original.phone : "-"),
        accessorKey: EmployeeColumnId.phone,
        ...ColumnSizes.status,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.phone_personal]: {
        id: EmployeeColumnId.phone_personal,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_phone_personal,
          t("core:phone_personal")
        ),
        cell: ({ row }) => (row.original.phone_personal ? row.original.phone_personal : "-"),
        accessorKey: EmployeeColumnId.phone_personal,
        ...ColumnSizes.status,
        enableSorting: true,
        sortDescFirst: false,
      },
      [EmployeeColumnId.slack]: {
        id: EmployeeColumnId.slack,
        header: fieldTitle(
          allSectionFields,
          ApiFieldTypes.employee_slack,
          t("integrations:slack_title")
        ),
        cell: ({ row }) =>
          row.original.slack ? (
            <LinkTo to={row.original.slack.url} target={"_blank"} variant={"plain"}>
              <ListItem
                preTitle={row.original.slack.name}
                caption={formatSlackHandle(row.original.slack.handle)}
                avatarUrl={row.original.slack.avatar_url ?? undefined}
                behavior={"card"}
                emphasis={"top"}
              />
            </LinkTo>
          ) : (
            "-"
          ),
        accessorKey: EmployeeColumnId.slack,
        ...ColumnSizes.employee,
        enableSorting: false,
      },
      [EmployeeColumnId.employee_position]: {
        id: EmployeeColumnId.employee_position,
        header: fieldTitle(
          positionFields,
          ApiFieldTypes.employee_position_job_profile,
          t("core:position")
        ),
        cell: ({ row }) => getEmployeePositionTitle(row.original) || "-",
        accessorKey: EmployeeColumnId.employee_position,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.job_role]: {
        id: EmployeeColumnId.job_role,
        header: t("core:job_role"),
        cell: ({ row }) => row.original.employee_position?.job_profile?.role?.title || "-",
        accessorKey: EmployeeColumnId.job_role,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.position_date_effective_from]: {
        id: EmployeeColumnId.position_date_effective_from,
        header: fieldTitle(
          positionFields,
          ApiFieldTypes.effective_from,
          t("core:position_effective_date")
        ),
        cell: ({ row }) =>
          row.original.employee_position?.date_effective_from
            ? renderDate(row.original.employee_position.date_effective_from)
            : "-",
        accessorKey: EmployeeColumnId.position_date_effective_from,
        ...ColumnSizes.date,
        enableSorting: false,
      },
      [EmployeeColumnId.department]: {
        id: EmployeeColumnId.department,
        header: fieldTitle(
          positionFields,
          ApiFieldTypes.employee_position_department,
          t("core:department")
        ),
        cell: ({ row }) => row.original.employee_position?.department?.title || "-",
        accessorKey: EmployeeColumnId.department,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.division]: {
        id: EmployeeColumnId.division,
        header: fieldTitle(
          positionFields,
          ApiFieldTypes.employee_position_division,
          t("core:division")
        ),
        cell: ({ row }) => row.original.employee_position?.division?.title || "-",
        accessorKey: EmployeeColumnId.division,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.manager]: {
        id: EmployeeColumnId.manager,
        header: fieldTitle(
          positionFields,
          ApiFieldTypes.employee_position_reporting_to,
          t("core:manager")
        ),
        cell: ({ row }) => {
          const manager = row.original.employee_position?.manager;
          return manager ? (
            <EmployeeHoverCard employee={manager} position={getEmployeePositionTitle(manager)}>
              <EmployeeInline employee={manager} />
            </EmployeeHoverCard>
          ) : (
            "-"
          );
        },
        accessorKey: EmployeeColumnId.manager,
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
        enableSorting: false,
      },
      [EmployeeColumnId.location]: {
        id: EmployeeColumnId.location,
        header: fieldTitle(
          positionFields,
          ApiFieldTypes.employee_position_location,
          t("common:location")
        ),
        cell: ({ row }) => row.original.employee_position?.location?.title || "-",
        accessorKey: EmployeeColumnId.location,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.legal_entity]: {
        id: EmployeeColumnId.legal_entity,
        header: fieldTitle(
          employmentFields,
          ApiFieldTypes.employee_employment_legal_entity,
          t("core:legal_entity")
        ),
        cell: ({ row }) => row.original.employee_employment?.legal_entity?.title || "-",
        accessorKey: EmployeeColumnId.legal_entity,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.contract]: {
        id: EmployeeColumnId.contract,
        header: fieldTitle(
          employmentFields,
          ApiFieldTypes.employee_employment_contract,
          t("core:contract")
        ),
        cell: ({ row }) => row.original.employee_employment?.employment_contract?.title || "-",
        accessorKey: EmployeeColumnId.contract,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.work_pattern]: {
        id: EmployeeColumnId.work_pattern,
        header: fieldTitle(
          employmentFields,
          ApiFieldTypes.employee_employment_work_pattern,
          t("core:work_pattern")
        ),
        cell: ({ row }) => row.original.employee_employment?.work_pattern?.title || "-",
        accessorKey: EmployeeColumnId.work_pattern,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.employment_type]: {
        id: EmployeeColumnId.employment_type,
        header: fieldTitle(
          employmentFields,
          ApiFieldTypes.employee_employment_type,
          t("core:employment_type")
        ),
        cell: ({ row }) => row.original.employee_employment?.employment_type?.title || "-",
        accessorKey: EmployeeColumnId.employment_type,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.employment_date_effective_from]: {
        id: EmployeeColumnId.employment_date_effective_from,
        header: fieldTitle(
          employmentFields,
          ApiFieldTypes.effective_from,
          t("core:employment_start_date")
        ),
        cell: ({ row }) =>
          row.original.employee_employment?.date_effective_from
            ? renderDate(row.original.employee_employment.date_effective_from)
            : "-",
        accessorKey: EmployeeColumnId.employment_date_effective_from,
        ...ColumnSizes.date,
        enableSorting: false,
      },
      [EmployeeColumnId.employment_date_effective_to]: {
        id: EmployeeColumnId.employment_date_effective_to,
        header: t("core:employment_end_date"),
        cell: ({ row }) =>
          row.original.employee_employment?.date_effective_to
            ? renderDate(row.original.employee_employment.date_effective_to)
            : "-",
        accessorKey: EmployeeColumnId.employment_date_effective_to,
        ...ColumnSizes.date,
        enableSorting: false,
      },
      [EmployeeColumnId.termination_reason]: {
        id: EmployeeColumnId.termination_reason,
        header: t("core:termination_reason"),
        cell: ({ row }) => row.original.employee_termination?.termination_reason?.title || "-",
        accessorKey: EmployeeColumnId.termination_reason,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.probation_period_end]: {
        id: EmployeeColumnId.probation_period_end,
        header: fieldTitle(
          employmentFields,
          ApiFieldTypes.employee_employment_probation_date_end,
          t("core:probation_till")
        ),
        cell: ({ row }) =>
          row.original.employee_employment?.date_probation_ends
            ? renderDate(row.original.employee_employment.date_probation_ends)
            : "-",
        accessorKey: EmployeeColumnId.probation_period_end,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.termination_leave_type]: {
        id: EmployeeColumnId.termination_leave_type,
        header: t("core:leave_type"),
        cell: ({ row }) => row.original.employee_termination?.termination_leave_type?.title || "-",
        accessorKey: EmployeeColumnId.termination_leave_type,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.last_working_date]: {
        id: EmployeeColumnId.last_working_date,
        header: t("core:last_working_day"),
        cell: ({ row }) =>
          row.original.employee_termination?.date_last_working
            ? renderDate(row.original.employee_termination.date_last_working)
            : "-",
        accessorKey: EmployeeColumnId.last_working_date,
        ...ColumnSizes.date,
        enableSorting: false,
      },
      [EmployeeColumnId.replacement_employee]: {
        id: EmployeeColumnId.replacement_employee,
        header: t("core:termination_handover_to"),
        cell: ({ row }) => {
          const replacementEmployee = row.original.employee_termination?.replacement_employee;
          return replacementEmployee ? (
            <EmployeeHoverCard employee={replacementEmployee}>
              <EmployeeInline employee={replacementEmployee} />
            </EmployeeHoverCard>
          ) : (
            "-"
          );
        },
        accessorKey: EmployeeColumnId.replacement_employee,
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
        enableSorting: false,
      },
      [EmployeeColumnId.eligible_for_rehire]: {
        id: EmployeeColumnId.eligible_for_rehire,
        header: t("core:eligible_for_rehire"),
        cell: ({ row }) => {
          const eligibleForRehire = row.original.employee_termination?.eligible_for_rehire;
          if (eligibleForRehire == null) return "-";
          return eligibleForRehire ? t("common:yes") : t("common:no");
        },
        accessorKey: EmployeeColumnId.eligible_for_rehire,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.timezone]: {
        id: EmployeeColumnId.timezone,
        header: fieldTitle(allSectionFields, ApiFieldTypes.employee_timezone, t("common:timezone")),
        cell: ({ row }) => {
          if (!row.original.timezone) return "-";

          const zonedTime = toZonedTime(now, row.original.timezone);
          return (
            <Stack>
              <Text>{renderTime(zonedTime)}</Text>
              <Text>{row.original.timezone}</Text>
            </Stack>
          );
        },
        accessorKey: EmployeeColumnId.timezone,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.department_manager]: {
        id: EmployeeColumnId.department_manager,
        header: t("core:department_manager"),
        cell: ({ row }) => {
          const departmentManager = row.original.employee_position?.department_manager;
          return departmentManager ? (
            <EmployeeHoverCard
              employee={departmentManager.employee}
              position={getEmployeePositionTitle(departmentManager.employee)}
            >
              <EmployeeInline employee={departmentManager.employee} />
            </EmployeeHoverCard>
          ) : (
            "-"
          );
        },
        accessorKey: EmployeeColumnId.department_manager,
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
        enableSorting: false,
      },
      [EmployeeColumnId.employee_position_change_reason]: {
        id: EmployeeColumnId.employee_position_change_reason,
        header: fieldTitle(
          positionFields,
          ApiFieldTypes.employee_position_change_reason,
          t("core:employee_position_change_reason")
        ),
        cell: ({ row }) => row.original.employee_position?.change_reason?.title || "-",
        accessorKey: EmployeeColumnId.employee_position_change_reason,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.base_salary_gross]: {
        id: EmployeeColumnId.base_salary_gross,
        header: fieldTitle(
          compensationFields,
          ApiFieldTypes.employee_compensation_base_salary_gross,
          t("core:base_salary_gross")
        ),
        cell: ({ row }) =>
          row.original.employee_compensation?.base_salary_gross
            ? renderCurrency(
                row.original.employee_compensation.base_salary_gross,
                row.original.employee_compensation.currency
              )
            : "-",
        accessorKey: EmployeeColumnId.base_salary_gross,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.base_salary_net]: {
        id: EmployeeColumnId.base_salary_net,
        header: fieldTitle(
          compensationFields,
          ApiFieldTypes.employee_compensation_base_salary_net,
          t("core:base_salary_net")
        ),
        cell: ({ row }) =>
          row.original.employee_compensation?.base_salary_net
            ? renderCurrency(
                row.original.employee_compensation.base_salary_net,
                row.original.employee_compensation.currency
              )
            : "-",
        accessorKey: EmployeeColumnId.base_salary_net,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.compensation_total_cost]: {
        id: EmployeeColumnId.compensation_total_cost,
        header: fieldTitle(
          compensationFields,
          ApiFieldTypes.employee_compensation_total_cost,
          t("core:compensation_total_cost_rollup")
        ),
        cell: ({ row }) => {
          const compensation = row.original.employee_compensation;
          const rollups = compensation?.rollups;
          return (
            <CompensationRollupValue
              value={
                rollups?.total_cost_actual_amount == null
                  ? null
                  : renderCurrency(rollups.total_cost_actual_amount, rollups.currency_code)
              }
              isIncomplete={compensation?.is_rollup_incomplete}
              isEstimated={compensation?.is_rollup_estimated}
            />
          );
        },
        accessorKey: EmployeeColumnId.compensation_total_cost,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.compensation_per_type]: {
        id: EmployeeColumnId.compensation_per_type,
        header: fieldTitle(
          compensationFields,
          ApiFieldTypes.employee_compensation_per_type,
          t("common:period")
        ),
        cell: ({ row }) =>
          row.original.employee_compensation?.per_type
            ? getEmployeeCompensationPerTypeTitle(row.original.employee_compensation.per_type)
            : "-",
        accessorKey: EmployeeColumnId.compensation_per_type,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.compensation_change_reason]: {
        id: EmployeeColumnId.compensation_change_reason,
        header: fieldTitle(
          compensationFields,
          ApiFieldTypes.employee_compensation_change_reason,
          t("core:compensation_change_reason")
        ),
        cell: ({ row }) => row.original.employee_compensation?.change_reason?.title || "-",

        accessorKey: EmployeeColumnId.compensation_change_reason,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.compensation_date_effective_from]: {
        id: EmployeeColumnId.compensation_date_effective_from,
        header: fieldTitle(
          compensationFields,
          ApiFieldTypes.effective_from,
          t("core:compensation_date")
        ),
        cell: ({ row }) =>
          row.original.employee_compensation?.date_effective_from
            ? renderDate(row.original.employee_compensation.date_effective_from)
            : "-",
        accessorKey: EmployeeColumnId.compensation_date_effective_from,
        ...ColumnSizes.date,
        enableSorting: false,
      },
      [EmployeeColumnId.compensation_currency]: {
        id: EmployeeColumnId.compensation_currency,
        header: t("common:currency"),
        cell: ({ row }) => row.original.employee_compensation?.currency || "-",
        accessorKey: EmployeeColumnId.compensation_currency,
        ...ColumnSizes.narrow,
        enableSorting: false,
      },
      [EmployeeColumnId.compensation_comment]: {
        id: EmployeeColumnId.compensation_comment,
        header: t("common:comment"),
        cell: ({ row }) => row.original.employee_compensation?.comment || "-",
        accessorKey: EmployeeColumnId.compensation_comment,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.position_comment]: {
        id: EmployeeColumnId.position_comment,
        header: t("common:comment"),
        cell: ({ row }) => row.original.employee_position?.comment || "-",
        accessorKey: EmployeeColumnId.position_comment,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      [EmployeeColumnId.employment_comment]: {
        id: EmployeeColumnId.employment_comment,
        header: t("common:comment"),
        cell: ({ row }) => row.original.employee_employment?.comment || "-",
        accessorKey: EmployeeColumnId.employment_comment,
        ...ColumnSizes.standard,
        enableSorting: false,
      },
      ...columnDefinitionsForCustomFields,
    };
  }, [now, allSectionFields, positionFields, employmentFields, compensationFields]);

  const employeesColumnConfig: ColumnConfig = useMemo(() => {
    const customColumns: ColumnDefinition[] = [];

    if (isFeatureEnabled(ApiFeatureType.custom_fields)) {
      allSections.forEach((sectionWrapper) => {
        sectionWrapper.fields.forEach((field) => {
          if (field.type == ApiFieldTypes.custom) {
            customColumns.push({
              id: field.slug,
              title: getFieldTitle(field),
              isFixed: false,
            });
          }
        });
      });
    }

    return {
      type: ApiColumnConfigType.employees_columns,
      categories: [
        {
          id: "personal",
          title: t("core:personal"),
          columns: [
            {
              id: EmployeeColumnId.employee_name,
              title: t("core:full_name"),
              isFixed: true,
            },
            {
              id: EmployeeColumnId.lifecycle_status,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_lifecycle_status,
                t("core:lifecycle_status")
              ),
            },
            {
              id: EmployeeColumnId.middle_name,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_middle_name,
                t("core:middle_name")
              ),
            },
            {
              id: EmployeeColumnId.name_pronunciation,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_name_pronunciation,
                t("common:name_pronunciation")
              ),
            },
            {
              id: EmployeeColumnId.last_seen,
              title: t("common:last_seen"),
            },
            {
              id: EmployeeColumnId.birth_date,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_date_birth,
                t("core:birth_date")
              ),
            },
            {
              id: EmployeeColumnId.personnel_number,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_number,
                t("core:employee_number")
              ),
            },
            {
              id: EmployeeColumnId.gender,
              title: fieldTitle(allSectionFields, ApiFieldTypes.employee_gender, t("core:gender")),
            },
            {
              id: EmployeeColumnId.address,
              title: t("core:address"),
            },
            {
              id: EmployeeColumnId.timezone,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_timezone,
                t("common:timezone")
              ),
            },
            {
              id: EmployeeColumnId.termination_date,
              title: t("core:termination_date"),
            },
          ],
        },
        {
          id: "contacts",
          title: t("core:contacts"),
          columns: [
            {
              id: EmployeeColumnId.email,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_email,
                t("core:work_email")
              ),
            },
            {
              id: EmployeeColumnId.email_personal,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_email_personal,
                t("core:email_personal")
              ),
            },
            {
              id: EmployeeColumnId.phone,
              title: fieldTitle(allSectionFields, ApiFieldTypes.employee_phone, t("core:phone")),
            },
            {
              id: EmployeeColumnId.phone_personal,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_phone_personal,
                t("core:phone_personal")
              ),
            },
            {
              id: EmployeeColumnId.slack,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_slack,
                t("integrations:slack_title")
              ),
            },
          ],
        },
        {
          id: "position",
          title: t("core:position"),
          columns: [
            {
              id: EmployeeColumnId.employee_position,
              title: fieldTitle(
                positionFields,
                ApiFieldTypes.employee_position_job_profile,
                t("core:position")
              ),
            },
            {
              id: EmployeeColumnId.position_date_effective_from,
              title: fieldTitle(
                positionFields,
                ApiFieldTypes.effective_from,
                t("core:position_effective_date")
              ),
            },
            {
              id: EmployeeColumnId.department,
              title: fieldTitle(
                positionFields,
                ApiFieldTypes.employee_position_department,
                t("core:department")
              ),
            },
            {
              id: EmployeeColumnId.division,
              title: fieldTitle(
                positionFields,
                ApiFieldTypes.employee_position_division,
                t("core:division")
              ),
            },
            {
              id: EmployeeColumnId.manager,
              title: fieldTitle(
                positionFields,
                ApiFieldTypes.employee_position_reporting_to,
                t("core:manager")
              ),
            },
            {
              id: EmployeeColumnId.location,
              title: fieldTitle(
                positionFields,
                ApiFieldTypes.employee_position_location,
                t("common:location")
              ),
            },
            {
              id: EmployeeColumnId.legal_entity,
              title: fieldTitle(
                employmentFields,
                ApiFieldTypes.employee_employment_legal_entity,
                t("core:legal_entity")
              ),
            },
            {
              id: EmployeeColumnId.department_manager,
              title: t("core:department_manager"),
            },
            {
              id: EmployeeColumnId.employee_position_change_reason,
              title: fieldTitle(
                positionFields,
                ApiFieldTypes.employee_position_change_reason,
                t("core:employee_position_change_reason")
              ),
            },
          ],
        },
        {
          id: "employment",
          title: t("core:employment"),
          columns: [
            {
              id: EmployeeColumnId.hired_on,
              title: fieldTitle(
                allSectionFields,
                ApiFieldTypes.employee_hired_on,
                t("core:hired_on")
              ),
            },
            {
              id: EmployeeColumnId.contract,
              title: fieldTitle(
                employmentFields,
                ApiFieldTypes.employee_employment_contract,
                t("core:contract")
              ),
            },
            {
              id: EmployeeColumnId.employment_type,
              title: fieldTitle(
                employmentFields,
                ApiFieldTypes.employee_employment_type,
                t("core:employment_type")
              ),
            },
            {
              id: EmployeeColumnId.employment_date_effective_from,
              title: fieldTitle(
                employmentFields,
                ApiFieldTypes.effective_from,
                t("core:employment_start_date")
              ),
            },
            {
              id: EmployeeColumnId.employment_date_effective_to,
              title: t("core:employment_end_date"),
            },
            {
              id: EmployeeColumnId.work_pattern,
              title: fieldTitle(
                employmentFields,
                ApiFieldTypes.employee_employment_work_pattern,
                t("core:work_pattern")
              ),
            },
            {
              id: EmployeeColumnId.termination_reason,
              title: t("core:termination_reason"),
            },
            {
              id: EmployeeColumnId.termination_leave_type,
              title: t("core:leave_type"),
            },
            {
              id: EmployeeColumnId.last_working_date,
              title: t("core:last_working_day"),
            },
            {
              id: EmployeeColumnId.replacement_employee,
              title: t("core:termination_handover_to"),
            },
            {
              id: EmployeeColumnId.eligible_for_rehire,
              title: t("core:eligible_for_rehire"),
            },
            {
              id: EmployeeColumnId.probation_period_end,
              title: fieldTitle(
                employmentFields,
                ApiFieldTypes.employee_employment_probation_date_end,
                t("core:probation_till")
              ),
            },
          ],
        },
        {
          id: "compensation",
          title: t("core:compensation"),
          columns: [
            {
              id: EmployeeColumnId.base_salary_gross,
              title: fieldTitle(
                compensationFields,
                ApiFieldTypes.employee_compensation_base_salary_gross,
                t("core:base_salary_gross")
              ),
            },
            {
              id: EmployeeColumnId.base_salary_net,
              title: fieldTitle(
                compensationFields,
                ApiFieldTypes.employee_compensation_base_salary_net,
                t("core:base_salary_net")
              ),
            },
            ...(canCurrentEmployeeViewField(
              EmployeeFieldPermission.employee_compensation_total_cost
            )
              ? [
                  {
                    id: EmployeeColumnId.compensation_total_cost,
                    title: fieldTitle(
                      compensationFields,
                      ApiFieldTypes.employee_compensation_total_cost,
                      t("core:compensation_total_cost_rollup")
                    ),
                  },
                ]
              : []),
            {
              id: EmployeeColumnId.compensation_date_effective_from,
              title: fieldTitle(
                compensationFields,
                ApiFieldTypes.effective_from,
                t("core:compensation_date")
              ),
            },
            {
              id: EmployeeColumnId.compensation_per_type,
              title: fieldTitle(
                compensationFields,
                ApiFieldTypes.employee_compensation_per_type,
                t("common:period")
              ),
            },
            {
              id: EmployeeColumnId.compensation_change_reason,
              title: fieldTitle(
                compensationFields,
                ApiFieldTypes.employee_compensation_change_reason,
                t("core:compensation_change_reason")
              ),
            },
          ],
        },
        {
          id: "custom_fields",
          title: t("custom_fields:custom_fields"),
          columns: customColumns,
        },
      ],
    };
  }, [
    appConfig,
    canCurrentEmployeeViewField,
    allSectionFields,
    positionFields,
    employmentFields,
    compensationFields,
  ]);

  return { columnDefinitions, employeesColumnConfig };
};
